/**
 * Performance multiplier chip (§5.3, §8.3, §12.5).
 *
 * Tier and shade are both server discriminants — no threshold lives here, only the attribute and
 * class name derived from them. `shade` drives the soft tinted chip (11 bands); a `null` shade
 * means the server has no usable multiplier, and the chip stays the quiet grey "—". `display`
 * ("20.3×" or "—") is rendered verbatim, and `insufficient` picks the muted treatment without the
 * client ever asking why.
 */

import type { MultiplierDTO, Tier } from '@lw/shared';
import { TooltipPopover } from './TooltipPopover';
import { cx } from './cx';

const TIER_CLASS: Record<Tier, string> = {
  low: 'mx--low',
  neutral: 'mx--neutral',
  notable: 'mx--notable',
  strong: 'mx--strong',
  viral: 'mx--viral',
};

export type MultiplierBadgeProps = {
  value: MultiplierDTO;
  /** Show the info affordance (multiplier tooltips are always present). Default `true`. */
  withTooltip?: boolean;
  tooltipLabel?: string;
  className?: string;
};

export function MultiplierBadge({
  value,
  withTooltip = true,
  tooltipLabel,
  className,
}: MultiplierBadgeProps) {
  const variant = value.insufficient
    ? 'mx--insufficient'
    : value.tier !== null
      ? TIER_CLASS[value.tier]
      : 'mx--none';

  return (
    <span
      className={cx('mx', variant, className)}
      data-tier={value.tier ?? undefined}
      data-shade={value.shade ?? undefined}
    >
      <span>{value.display}</span>
      {withTooltip ? <TooltipPopover tooltip={value.tooltip} label={tooltipLabel} /> : null}
    </span>
  );
}

export default MultiplierBadge;
